import {
  Component,
  type ComponentType,
  type ErrorInfo,
  type ReactNode,
} from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
  onError?: (error: Error) => void
  fallback?: ComponentType<{ error: Error; resetErrorBoundary?: () => void }>
  resetKeys?: unknown[]
  onReset?: () => void
}

interface ErrorBoundaryState {
  error: Error | null
  resetKeys: unknown[] | undefined
}

function didResetKeysChange(
  previous: unknown[] | undefined,
  current: unknown[] | undefined,
): boolean {
  return Boolean(
    previous &&
    current &&
    (current.length !== previous.length ||
      current.some((key, index) => key !== previous[index])),
  )
}

// motivation react-error-boundary v6 doesn't support react native https://github.com/bvaughn/react-error-boundary/issues/219
export class BasicErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null, resetKeys: props.resetKeys }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKeys === state.resetKeys) return null

    return {
      resetKeys: props.resetKeys,
      ...(state.error && didResetKeysChange(state.resetKeys, props.resetKeys)
        ? { error: null }
        : {}),
    }
  }

  override componentDidUpdate(
    prevProps: ErrorBoundaryProps,
    prevState: ErrorBoundaryState,
  ): void {
    if (
      prevState.error !== null &&
      this.state.error === null &&
      didResetKeysChange(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.props.onReset?.()
    }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    ;(error as Error & { componentStack?: string }).componentStack =
      errorInfo.componentStack ?? undefined
    this.props.onError?.(error)
  }

  resetErrorBoundary = (): void => {
    this.props.onReset?.()
    this.setState({ error: null })
  }

  override render(): ReactNode {
    if (this.state.error) {
      const Fallback = this.props.fallback
      if (Fallback) {
        return (
          <Fallback
            error={this.state.error}
            resetErrorBoundary={this.resetErrorBoundary}
          />
        )
      }
      return (
        <p style={{ wordBreak: 'break-word' }}>
          Error: {this.state.error.message}
        </p>
      )
    }

    return this.props.children
  }
}
